import React, { useState, useEffect, useMemo } from 'react';
import {
  AcademicCapIcon,
  UserGroupIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  BoltIcon,
  CheckCircleIcon,
  ArrowsUpDownIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArchiveBoxIcon,
  ShieldExclamationIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import { ExamScheduleConfig, ExamAttemptRecord, ExamQuestion, ExamId } from '../../types/exam';
import { EXAM_SCHEDULES } from '../../data/examQuestions';
import { EXAM_1_ARCHIVE } from '../../data/exam1Archive';
import { TOTAL_REGISTERED_STUDENTS_COUNT } from '../../data/registeredCandidates';
import { 
  subscribeToExamAttempts, 
  exportExamReportToExcel, 
  getExamPortalPhase,
  forceSubmitAllActiveExams,
  getCurrentISTMinutes,
  buildConsolidatedMeritList,
  exportConsolidatedMeritListToExcel,
} from '../../services/examService';

type SortField = 'score' | 'name' | 'regNo' | 'loginTime' | 'answers' | 'percentage';
type SortOrder = 'asc' | 'desc';
type ExamActivityFilter = 'all' | 'actually_gave_exam' | 'lobby_only' | 'qualified' | 'needs_improvement';

const AdminExamManagement: React.FC = () => {
  // Toggle between Assessment Set 2, Assessment Set 1, or Consolidated Merit Hub
  const [selectedExamId, setSelectedExamId] = useState<ExamId | 'combined_merit_list'>('final_exam_set2');
  const currentExamConfig: ExamScheduleConfig = EXAM_SCHEDULES[selectedExamId as ExamId] || EXAM_SCHEDULES.final_exam_set2 || EXAM_SCHEDULES.final_exam;

  const [liveAttempts, setLiveAttempts] = useState<ExamAttemptRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activityFilter, setActivityFilter] = useState<ExamActivityFilter>('all');
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [inspectAttempt, setInspectAttempt] = useState<ExamAttemptRecord | any | null>(null);
  const [isForceSubmitting, setIsForceSubmitting] = useState(false);
  const [forceSubmitMessage, setForceSubmitMessage] = useState<string | null>(null);

  // Consolidated Merit List State & Filtering
  const [meritTierFilter, setMeritTierFilter] = useState<'all' | 'distinction' | 'first_class' | 'qualified' | 'participated'>('all');
  const [meritSearch, setMeritSearch] = useState('');

  const consolidatedList = useMemo(() => {
    return buildConsolidatedMeritList(liveAttempts);
  }, [liveAttempts]);

  const filteredConsolidatedList = useMemo(() => {
    return consolidatedList.filter((c) => {
      if (meritTierFilter === 'distinction' && c.bestPct < 80) return false;
      if (meritTierFilter === 'first_class' && (c.bestPct < 60 || c.bestPct >= 80)) return false;
      if (meritTierFilter === 'qualified' && (c.bestPct < 50 || c.bestPct >= 60)) return false;
      if (meritTierFilter === 'participated' && c.bestPct >= 50) return false;

      if (meritSearch.trim()) {
        const q = meritSearch.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.regNo.toLowerCase().includes(q) ||
          c.mobile.includes(q) ||
          c.qualificationTier.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [consolidatedList, meritTierFilter, meritSearch]);

  // Real-time listener for current exam attempts (only for live exams)
  useEffect(() => {
    const unsub = subscribeToExamAttempts('final_exam_set2', (list) => {
      setLiveAttempts(list);
    });
    return () => unsub();
  }, []);

  const handleForceSubmitAll = async () => {
    if (!window.confirm('Are you sure you want to FORCE SUBMIT all candidate examinations? This will calculate scores from current answers and finalize test status.')) {
      return;
    }
    setIsForceSubmitting(true);
    setForceSubmitMessage(null);
    try {
      const res = await forceSubmitAllActiveExams(currentExamConfig);
      setForceSubmitMessage(`Successfully finalized and submitted ${res.count} candidate attempt(s). Examination officially closed.`);
      setTimeout(() => setForceSubmitMessage(null), 6000);
    } catch (err: any) {
      alert('Error during force submission: ' + (err.message || 'Unknown error'));
    } finally {
      setIsForceSubmitting(false);
    }
  };

  // Current phase calculation for the selected exam
  const [phaseInfo, setPhaseInfo] = useState(() => getExamPortalPhase(currentExamConfig));
  useEffect(() => {
    const update = () => {
      setPhaseInfo(getExamPortalPhase(currentExamConfig));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [currentExamConfig]);

  // Attempts pool based on active exam tab
  const rawAttempts: any[] = useMemo(() => {
    if (selectedExamId === 'final_exam') {
      return (EXAM_1_ARCHIVE.candidates || []).map((c: any) => {
        const totalQ = c.totalMarks || 30;
        const correct = c.correctCount || 0;
        const wrong = c.wrongCount || 0;
        const unattempted = Math.max(0, totalQ - correct - wrong);
        const gaveExam = c.actuallyGaveExam !== undefined ? c.actuallyGaveExam : ((correct + wrong) > 0 || (c.score || 0) > 0);

        return {
          attemptId: `exam1_${c.registrationNumber}`,
          examId: 'final_exam',
          studentRegistrationNumber: c.registrationNumber,
          studentRegNoSuffix: c.registrationNumber.slice(-4),
          studentName: c.studentName,
          studentMobile: c.mobileNumber,
          studentBatchLabel: c.batch,
          loginTime: c.loginTime,
          loginTimestamp: 0,
          status: 'submitted',
          score: c.score,
          totalQuestions: totalQ,
          correctCount: correct,
          wrongCount: wrong,
          unattemptedCount: unattempted,
          percentage: c.percentage,
          actuallyGaveExam: gaveExam,
          answers: {},
          detailedResponses: c.detailedResponses,
          tabSwitchCount: 0,
          copyPasteAttempts: 0,
          aiSuspicionLevel: 'NORMAL',
          aiSuspicionScore: 0,
          isLiveActive: false,
        };
      });
    }

    return liveAttempts.filter((att) => {
      const reg = (att.studentRegistrationNumber || '').toUpperCase();
      const suffix = (att.studentRegNoSuffix || '').toUpperCase();
      const name = (att.studentName || '').toLowerCase();
      const isDemo =
        reg.includes('999') ||
        suffix === '999' ||
        suffix === '0999' ||
        name.includes('demo') ||
        name.includes('vishal thakur') ||
        att.studentMobile === '9999999999';
      return !isDemo;
    });
  }, [selectedExamId, liveAttempts]);

  // Statistics
  const isExamConcluded = phaseInfo.phase === 'EXAM_ENDED' || getCurrentISTMinutes() >= (14 * 60 + 15);
  const totalRegistered = selectedExamId === 'final_exam' ? 101 : TOTAL_REGISTERED_STUDENTS_COUNT;
  const totalEnrolled = rawAttempts.length;
  const liveActiveCount = isExamConcluded ? 0 : rawAttempts.filter(a => a.status === 'in_progress' || a.isLiveActive).length;
  const aiSuspiciousCount = rawAttempts.filter(a => a.aiSuspicionLevel === 'HIGH_RISK_AI_SUSPECTED' || a.aiSuspicionLevel === 'SUSPICIOUS' || (a.tabSwitchCount && a.tabSwitchCount > 0)).length;
  const actuallyGaveExamCount = rawAttempts.filter(a => a.actuallyGaveExam || (a.answers && Object.keys(a.answers).length > 0) || ((a.correctCount || 0) + (a.wrongCount || 0) > 0) || a.score > 0).length;
  const lobbyOnlyCount = Math.max(0, totalEnrolled - actuallyGaveExamCount);
  const qualifiedCount = rawAttempts.filter(a => a.percentage >= 50).length;
  const avgScore = actuallyGaveExamCount > 0 
    ? Math.round((rawAttempts.filter(a => a.actuallyGaveExam || (a.answers && Object.keys(a.answers).length > 0) || ((a.correctCount || 0) + (a.wrongCount || 0) > 0) || a.score > 0).reduce((acc, curr) => acc + (curr.score || 0), 0) / actuallyGaveExamCount) * 10) / 10
    : 0;

  // Handle header click to toggle sort
  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Filtered and sorted candidate attempts
  const processedAttempts = useMemo(() => {
    let list = rawAttempts.filter((att) => {
      const q = searchQuery.toLowerCase().trim();
      const matchSearch =
        !q ||
        (att.studentName || '').toLowerCase().includes(q) ||
        (att.studentRegistrationNumber || '').toLowerCase().includes(q) ||
        (att.studentMobile || '').includes(q);

      const answeredCount = Object.keys(att.answers || {}).length || ((att.correctCount || 0) + (att.wrongCount || 0)) || (att.score > 0 ? 30 : 0);
      const isExamGiven = att.actuallyGaveExam !== undefined ? att.actuallyGaveExam : (answeredCount > 0 || att.score > 0);
      let matchFilter = true;

      if (activityFilter === 'actually_gave_exam') {
        matchFilter = isExamGiven;
      } else if (activityFilter === 'lobby_only') {
        matchFilter = !isExamGiven;
      } else if (activityFilter === 'qualified') {
        matchFilter = att.percentage >= 50;
      } else if (activityFilter === 'needs_improvement') {
        matchFilter = isExamGiven && att.percentage < 50;
      }

      return matchSearch && matchFilter;
    });

    list.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'score') {
        comparison = (b.score || 0) - (a.score || 0);
        if (comparison === 0) {
          const aCount = Object.keys(a.answers || {}).length || ((a.correctCount || 0) + (a.wrongCount || 0));
          const bCount = Object.keys(b.answers || {}).length || ((b.correctCount || 0) + (b.wrongCount || 0));
          comparison = bCount - aCount;
        }
      } else if (sortField === 'answers') {
        const aCount = Object.keys(a.answers || {}).length || ((a.correctCount || 0) + (a.wrongCount || 0));
        const bCount = Object.keys(b.answers || {}).length || ((b.correctCount || 0) + (b.wrongCount || 0));
        comparison = bCount - aCount;
      } else if (sortField === 'percentage') {
        comparison = (b.percentage || 0) - (a.percentage || 0);
      } else if (sortField === 'name') {
        comparison = (a.studentName || '').localeCompare(b.studentName || '');
      } else if (sortField === 'regNo') {
        comparison = (a.studentRegistrationNumber || '').localeCompare(b.studentRegistrationNumber || '');
      } else if (sortField === 'loginTime') {
        comparison = (b.loginTimestamp || 0) - (a.loginTimestamp || 0);
      }

      return sortOrder === 'desc' ? comparison : -comparison;
    });

    return list;
  }, [rawAttempts, searchQuery, activityFilter, sortField, sortOrder]);

  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowsUpDownIcon className="w-3.5 h-3.5 text-slate-500 opacity-60 inline ml-1" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUpIcon className="w-3.5 h-3.5 text-amber-400 inline ml-1" />
    ) : (
      <ChevronDownIcon className="w-3.5 h-3.5 text-amber-400 inline ml-1" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Multi-Exam Navigation Selector */}
      <div className="bg-slate-950/80 border border-white/10 p-2 rounded-2xl flex flex-wrap items-center justify-between gap-3 backdrop-blur-xl">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setSelectedExamId('final_exam_set2')}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all flex items-center space-x-2 ${
              selectedExamId === 'final_exam_set2'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 shadow-lg shadow-amber-500/20'
                : 'text-slate-300 hover:text-white hover:bg-white/10'
            }`}
          >
            <AcademicCapIcon className="w-4 h-4 shrink-0" />
            <span>Assessment Set 2 (Current / Live)</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-black uppercase bg-slate-950 text-amber-400 ml-1">
              Live
            </span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedExamId('final_exam')}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all flex items-center space-x-2 ${
              selectedExamId === 'final_exam'
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg shadow-indigo-500/20'
                : 'text-slate-300 hover:text-white hover:bg-white/10'
            }`}
          >
            <ArchiveBoxIcon className="w-4 h-4 shrink-0" />
            <span>Assessment Set 1 (18 Oct 2026)</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/20 text-blue-100 ml-1">
              Archived ({EXAM_1_ARCHIVE.totalCandidates} records)
            </span>
          </button>

          <button
            type="button"
            onClick={() => setSelectedExamId('combined_merit_list')}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all flex items-center space-x-2 ${
              selectedExamId === 'combined_merit_list'
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                : 'text-slate-300 hover:text-white hover:bg-white/10'
            }`}
          >
            <TrophyIcon className="w-4 h-4 shrink-0" />
            <span>🏆 Consolidated Merit List (Set 1 + Set 2)</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-black uppercase bg-slate-950 text-emerald-400 ml-1">
              Merit Hub
            </span>
          </button>
        </div>

        {selectedExamId === 'final_exam' && (
          <span className="text-xs text-emerald-400 font-bold px-3 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center space-x-1.5">
            <CheckCircleIcon className="w-4 h-4" />
            <span>Set 1 Data 100% Preserved &amp; Backed Up</span>
          </span>
        )}
      </div>

      {selectedExamId === 'combined_merit_list' ? (
        <div className="space-y-6">
          {/* Consolidated Header Hero Card */}
          <div className="bg-gradient-to-br from-slate-900 via-emerald-950/60 to-slate-900 border border-emerald-500/40 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden backdrop-blur-xl">
            <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="space-y-2">
                <div className="flex items-center space-x-2.5 flex-wrap gap-y-1.5">
                  <span className="px-3 py-1 rounded-full text-xs font-black tracking-wider bg-emerald-500 text-slate-950 uppercase shadow-md flex items-center space-x-1.5">
                    <TrophyIcon className="w-4 h-4" />
                    <span>CONSOLIDATED MERIT HUB</span>
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-emerald-200 border border-emerald-500/30">
                    Set 1 (18 Oct) + Set 2 Unified
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                    Official Merit Roster
                  </span>
                </div>

                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  Kreed Odisha Phase 2 — Consolidated Merit List &amp; Scorecards
                </h1>

                <p className="text-xs sm:text-sm text-emerald-100/80 max-w-2xl leading-relaxed">
                  Unified candidate evaluation merging Assessment Set 1 &amp; Assessment Set 2. Best performance marks, pass/qualification evaluation, and certificate issuance roster.
                </p>
              </div>

              {/* Action Button */}
              <button
                onClick={() => exportConsolidatedMeritListToExcel(liveAttempts)}
                className="px-5 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-xl transition flex items-center justify-center space-x-2 cursor-pointer self-start md:self-auto active:scale-95"
              >
                <ArrowDownTrayIcon className="w-5 h-5 text-slate-950" />
                <span>Download Consolidated Merit List (.xlsx)</span>
              </button>
            </div>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-slate-900 border border-white/10 rounded-2xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total Evaluated</p>
              <p className="text-2xl sm:text-3xl font-black text-white mt-0.5">{consolidatedList.length}</p>
              <p className="text-[10px] text-emerald-400 font-semibold mt-1">Unique Candidate Records</p>
            </div>
            <div className="bg-slate-900 border border-white/10 rounded-2xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total Qualified (&gt;=50%)</p>
              <p className="text-2xl sm:text-3xl font-black text-emerald-400 mt-0.5">
                {consolidatedList.filter(c => c.isEligibleForCertificate).length}
              </p>
              <p className="text-[10px] text-slate-400 font-semibold mt-1">
                {consolidatedList.length > 0 ? Math.round((consolidatedList.filter(c => c.isEligibleForCertificate).length / consolidatedList.length) * 100) : 0}% Qualification Rate
              </p>
            </div>
            <div className="bg-slate-900 border border-white/10 rounded-2xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Distinctions (&gt;=80%)</p>
              <p className="text-2xl sm:text-3xl font-black text-amber-400 mt-0.5">
                {consolidatedList.filter(c => c.bestPct >= 80).length}
              </p>
              <p className="text-[10px] text-amber-400/80 font-semibold mt-1">Top Honor Candidates</p>
            </div>
            <div className="bg-slate-900 border border-white/10 rounded-2xl p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Average Best Score</p>
              <p className="text-2xl sm:text-3xl font-black text-indigo-400 mt-0.5">
                {consolidatedList.length > 0 ? (consolidatedList.reduce((acc, c) => acc + c.bestScore, 0) / consolidatedList.length).toFixed(1) : 0} <span className="text-xs text-slate-400">/ 30</span>
              </p>
              <p className="text-[10px] text-slate-400 font-semibold mt-1">Across Evaluated Pool</p>
            </div>
          </div>

          {/* Search & Tier Filters */}
          <div className="bg-slate-900 border border-white/10 p-4 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search merit list by candidate name, reg ID, mobile..."
                value={meritSearch}
                onChange={(e) => setMeritSearch(e.target.value)}
                className="w-full bg-slate-950/80 border border-white/15 rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 font-medium"
              />
              {meritSearch && (
                <button
                  onClick={() => setMeritSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="flex items-center space-x-1.5 overflow-x-auto no-scrollbar">
              <button
                type="button"
                onClick={() => setMeritTierFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${meritTierFilter === 'all' ? 'bg-emerald-500 text-slate-950' : 'bg-white/10 text-slate-300 hover:text-white'}`}
              >
                All ({consolidatedList.length})
              </button>
              <button
                type="button"
                onClick={() => setMeritTierFilter('distinction')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${meritTierFilter === 'distinction' ? 'bg-amber-400 text-slate-950' : 'bg-white/10 text-slate-300 hover:text-white'}`}
              >
                Distinction ({consolidatedList.filter(c => c.bestPct >= 80).length})
              </button>
              <button
                type="button"
                onClick={() => setMeritTierFilter('first_class')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${meritTierFilter === 'first_class' ? 'bg-indigo-400 text-slate-950' : 'bg-white/10 text-slate-300 hover:text-white'}`}
              >
                First Class ({consolidatedList.filter(c => c.bestPct >= 60 && c.bestPct < 80).length})
              </button>
              <button
                type="button"
                onClick={() => setMeritTierFilter('qualified')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${meritTierFilter === 'qualified' ? 'bg-emerald-400 text-slate-950' : 'bg-white/10 text-slate-300 hover:text-white'}`}
              >
                Qualified ({consolidatedList.filter(c => c.bestPct >= 50 && c.bestPct < 60).length})
              </button>
              <button
                type="button"
                onClick={() => setMeritTierFilter('participated')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${meritTierFilter === 'participated' ? 'bg-rose-400 text-slate-950' : 'bg-white/10 text-slate-300 hover:text-white'}`}
              >
                Participation ({consolidatedList.filter(c => c.bestPct < 50).length})
              </button>
            </div>
          </div>

          {/* Consolidated Merit Roster Table */}
          <div className="bg-slate-900 border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                    <th className="py-3 px-4">Rank</th>
                    <th className="py-3 px-4">Candidate Details</th>
                    <th className="py-3 px-4">Set 1 Score</th>
                    <th className="py-3 px-4">Set 2 Score</th>
                    <th className="py-3 px-4">Best Score</th>
                    <th className="py-3 px-4">Performance Tier</th>
                    <th className="py-3 px-4">Certificate Award</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredConsolidatedList.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-400 text-xs">
                        No candidate records found matching current search/filter.
                      </td>
                    </tr>
                  ) : (
                    filteredConsolidatedList.map((c) => (
                      <tr key={c.regNo} className="hover:bg-white/5 transition-all">
                        <td className="py-3 px-4 font-mono font-bold text-white">
                          {c.rank === 1 ? '🥇 1' : c.rank === 2 ? '🥈 2' : c.rank === 3 ? '🥉 3' : `#${c.rank}`}
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-bold text-white text-sm">{c.name}</p>
                          <div className="flex items-center space-x-2 text-[11px] text-slate-400 font-mono mt-0.5">
                            <span className="text-amber-400 font-bold">{c.regNo}</span>
                            {c.mobile && <span>• {c.mobile}</span>}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono font-bold">
                          {c.set1Score !== null ? (
                            <span className="text-indigo-300">{c.set1Score} / 30 ({c.set1Pct}%)</span>
                          ) : (
                            <span className="text-slate-600 font-normal">ABSENT</span>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono font-bold">
                          {c.set2Score !== null ? (
                            <span className="text-amber-300">{c.set2Score} / 30 ({c.set2Pct}%)</span>
                          ) : (
                            <span className="text-slate-600 font-normal">ABSENT</span>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono">
                          <span className="text-base font-black text-emerald-400">{c.bestScore} / 30</span>
                          <span className="text-[11px] text-slate-400 block font-semibold">({c.bestPct}%)</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                            c.qualificationTier.includes('Distinction')
                              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                              : c.qualificationTier.includes('First Class')
                                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40'
                                : c.qualificationTier.includes('Qualified')
                                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                  : 'bg-slate-700/40 text-slate-400 border border-slate-700'
                          }`}>
                            {c.qualificationTier}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {c.isEligibleForCertificate ? (
                            <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              <span>Eligible for Certificate</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-slate-800 text-slate-400">
                              <span>Participation Record</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
      <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950/80 border border-indigo-500/30 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden backdrop-blur-xl">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center space-x-2.5 flex-wrap gap-y-1.5">
              <span className="px-3 py-1 rounded-full text-xs font-black tracking-wider bg-indigo-500 text-white uppercase shadow-md flex items-center space-x-1.5">
                <AcademicCapIcon className="w-4 h-4" />
                <span>EXAM SUPERVISION CONSOLE</span>
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                phaseInfo.phase === 'EXAM_ACTIVE'
                  ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 animate-pulse'
                  : phaseInfo.phase === 'EXAM_ENDED'
                    ? 'bg-slate-500/20 text-slate-300 border border-slate-500/40'
                    : phaseInfo.phase === 'LOBBY_OPEN'
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                      : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40'
              }`}>
                ● {phaseInfo.phase === 'EXAM_ACTIVE' ? 'Live Exam Underway' : phaseInfo.phase === 'EXAM_ENDED' ? 'Exam Concluded' : phaseInfo.phase === 'LOBBY_OPEN' ? 'Lobby Open' : 'Scheduled'}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                Unified Examination • All Batches Combined
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              {currentExamConfig.title}
            </h1>

            <p className="text-xs sm:text-sm text-indigo-100/80 max-w-2xl leading-relaxed">
              {currentExamConfig.subtitle} • Real-time supervision, attendance, score computations, and verified report export.
            </p>
          </div>

          {/* Header Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 self-start md:self-auto">
            {selectedExamId === 'final_exam_set2' && (
              <button
                onClick={handleForceSubmitAll}
                disabled={isForceSubmitting}
                className="px-4 py-3 rounded-2xl bg-gradient-to-r from-amber-400 to-rose-500 hover:from-amber-300 hover:to-rose-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-xl transition flex items-center justify-center space-x-2 disabled:opacity-50 cursor-pointer"
                title="Force submit all candidates and close examination"
              >
                <BoltIcon className="w-5 h-5 text-slate-950" />
                <span>{isForceSubmitting ? 'Finalizing Submissions...' : (isExamConcluded ? '⚡ Submissions Finalized & Locked' : '⚡ Finalize & Close All Exams')}</span>
              </button>
            )}

            {/* Export to Excel CTA */}
            <button
              onClick={() => exportExamReportToExcel(rawAttempts, currentExamConfig)}
              className="px-5 py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider shadow-xl transition flex items-center justify-center space-x-2 cursor-pointer"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>Export Results &amp; Answers (.xlsx)</span>
            </button>
          </div>
        </div>

        {forceSubmitMessage && (
          <div className="mt-4 p-3 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-xs font-bold flex items-center space-x-2 animate-fade-in">
            <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
            <span>{forceSubmitMessage}</span>
          </div>
        )}
      </div>

      {/* Real-Time Examination Status & Schedule Card */}
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-6 backdrop-blur-xl shadow-xl space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div className="flex items-center space-x-3.5">
            <span className={`w-3.5 h-3.5 rounded-full ${phaseInfo.phase === 'EXAM_ACTIVE' ? 'bg-emerald-400 animate-ping' : 'bg-amber-400'}`} />
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">
                  Examination Window:
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  Unified Candidate Batch
                </span>
              </div>
              <h2 className="text-lg sm:text-xl font-black text-white">
                {currentExamConfig.dateDisplay}
              </h2>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-xs text-slate-400">Current Phase:</span>
            <span className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider border shadow ${
              !isExamConcluded && phaseInfo.phase === 'EXAM_ACTIVE'
                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50'
                : !isExamConcluded && phaseInfo.phase === 'LOBBY_OPEN'
                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/50'
                  : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
            }`}>
              {!isExamConcluded && phaseInfo.phase === 'EXAM_ACTIVE' ? '🔴 EXAM ACTIVE (13:15 – 14:00 IST)' :
               !isExamConcluded && phaseInfo.phase === 'LOBBY_OPEN' ? '🟡 LOBBY OPEN (13:00 – 13:15 IST)' :
               !isExamConcluded && phaseInfo.phase === 'LOCKED_BEFORE_LOGIN' ? '🔒 LOCKED (WAITING FOR 13:00 IST)' :
               '✅ EXAM CONCLUDED (ALL SESSIONS FINALIZED)'}
            </span>
          </div>
        </div>

        {/* Schedule & Guidelines Badge Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div className="bg-black/40 border border-white/10 rounded-2xl p-3">
            <span className="text-[10px] text-slate-400 uppercase font-semibold block mb-0.5">Exam Date</span>
            <strong className="text-white text-xs sm:text-sm">{currentExamConfig.dateDisplay}</strong>
          </div>
          <div className="bg-black/40 border border-amber-500/20 rounded-2xl p-3">
            <span className="text-[10px] text-amber-400 uppercase font-semibold block mb-0.5">Lobby Entry Window</span>
            <strong className="text-amber-300 text-xs sm:text-sm">{currentExamConfig.loginOpenTime} – {currentExamConfig.loginCloseTime} IST</strong>
          </div>
          <div className="bg-black/40 border border-emerald-500/20 rounded-2xl p-3">
            <span className="text-[10px] text-emerald-400 uppercase font-semibold block mb-0.5">Live Exam Window</span>
            <strong className="text-emerald-300 text-xs sm:text-sm">{currentExamConfig.examStartTime} – {currentExamConfig.examEndTime} IST</strong>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-2xl p-3">
            <span className="text-[10px] text-slate-400 uppercase font-semibold block mb-0.5">Paper Configuration</span>
            <strong className="text-white text-xs sm:text-sm">{currentExamConfig.totalQuestions} MCQs / {currentExamConfig.totalQuestions} Marks</strong>
          </div>
        </div>
      </div>

      {/* Metrics Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 sm:gap-4">
        <div className="bg-slate-900 border border-white/10 rounded-2xl p-4 shadow-lg">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Total Registered</span>
          <span className="text-2xl sm:text-3xl font-black text-white mt-1 block">{totalRegistered}</span>
          <span className="text-[10px] text-slate-400">{totalEnrolled} reported in portal</span>
        </div>

        <div className="bg-slate-900 border border-emerald-500/40 rounded-2xl p-4 shadow-lg relative overflow-hidden">
          <div className="flex items-center space-x-1.5">
            {liveActiveCount > 0 && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />}
            <span className="text-[11px] font-black text-emerald-300 uppercase tracking-wider block">Live In-Test Now</span>
          </div>
          <span className="text-2xl sm:text-3xl font-black text-emerald-400 mt-1 block">{liveActiveCount}</span>
          <span className="text-[10px] text-emerald-300/80">{isExamConcluded ? 'Concluded • 0 Active' : 'Active right now'}</span>
        </div>

        <div className="bg-slate-900 border border-rose-500/40 rounded-2xl p-4 shadow-lg">
          <div className="flex items-center space-x-1">
            <ShieldExclamationIcon className="w-3.5 h-3.5 text-rose-400" />
            <span className="text-[11px] font-black text-rose-300 uppercase tracking-wider block">AI / Cheating Flag</span>
          </div>
          <span className="text-2xl sm:text-3xl font-black text-rose-400 mt-1 block">{aiSuspiciousCount}</span>
          <span className="text-[10px] text-rose-300/80">Tab switch alerts</span>
        </div>

        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-lg">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Lobby Only</span>
          <span className="text-2xl sm:text-3xl font-black text-slate-300 mt-1 block">{lobbyOnlyCount}</span>
          <span className="text-[10px] text-slate-400">Joined but 0 answers</span>
        </div>

        <div className="bg-slate-900 border border-amber-500/30 rounded-2xl p-4 shadow-lg">
          <span className="text-[11px] font-bold text-amber-300 uppercase tracking-wider block">Qualified (&ge;50%)</span>
          <span className="text-2xl sm:text-3xl font-black text-amber-400 mt-1 block">{qualifiedCount}</span>
          <span className="text-[10px] text-amber-300/80">{Math.round((qualifiedCount / (actuallyGaveExamCount || 1)) * 100)}% pass rate</span>
        </div>

        <div className="bg-slate-900 border border-indigo-500/30 rounded-2xl p-4 shadow-lg col-span-2 sm:col-span-1">
          <span className="text-[11px] font-bold text-indigo-300 uppercase tracking-wider block">Average Score</span>
          <span className="text-2xl sm:text-3xl font-black text-indigo-300 mt-1 block">{avgScore}</span>
          <span className="text-[10px] text-indigo-200/80">Out of {currentExamConfig.totalQuestions} marks</span>
        </div>
      </div>

      {/* Candidates Roster Table */}
      <div className="bg-slate-900 border border-white/10 rounded-3xl p-5 sm:p-6 backdrop-blur-xl shadow-xl space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="w-5 h-5 text-indigo-400" />
            <h2 className="text-base font-bold text-white uppercase tracking-wider">
              Candidates Examination Supervision ({processedAttempts.length})
            </h2>
          </div>

          {/* Filters & Sorting Controls */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search candidate, Reg ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-1.5 rounded-xl bg-black/40 border border-white/10 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 w-48 sm:w-56"
              />
            </div>

            {/* Activity Filter */}
            <div className="flex items-center space-x-1.5 bg-black/40 border border-white/10 rounded-xl px-2.5 py-1">
              <FunnelIcon className="w-3.5 h-3.5 text-indigo-400" />
              <select
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value as any)}
                className="bg-transparent text-xs text-white focus:outline-none cursor-pointer"
              >
                <option value="all" className="bg-slate-900">All Candidates ({rawAttempts.length})</option>
                <option value="actually_gave_exam" className="bg-slate-900">Actually Gave Exam ({actuallyGaveExamCount})</option>
                <option value="lobby_only" className="bg-slate-900">Lobby / Attendance Only ({lobbyOnlyCount})</option>
                <option value="qualified" className="bg-slate-900">Qualified (&ge;50%) ({qualifiedCount})</option>
                <option value="needs_improvement" className="bg-slate-900">Needs Improvement (&lt;50%) ({actuallyGaveExamCount - qualifiedCount})</option>
              </select>
            </div>

            {/* Sort Quick Selector */}
            <div className="flex items-center space-x-1.5 bg-black/40 border border-white/10 rounded-xl px-2.5 py-1">
              <ArrowsUpDownIcon className="w-3.5 h-3.5 text-amber-400" />
              <select
                value={`${sortField}_${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('_');
                  setSortField(field as SortField);
                  setSortOrder(order as SortOrder);
                }}
                className="bg-transparent text-xs text-white focus:outline-none cursor-pointer"
              >
                <option value="score_desc" className="bg-slate-900">Sort by: Highest Score</option>
                <option value="score_asc" className="bg-slate-900">Sort by: Lowest Score</option>
                <option value="answers_desc" className="bg-slate-900">Sort by: Most Answers</option>
                <option value="name_asc" className="bg-slate-900">Sort by: Name (A to Z)</option>
                <option value="name_desc" className="bg-slate-900">Sort by: Name (Z to A)</option>
                <option value="regNo_asc" className="bg-slate-900">Sort by: Reg ID (Asc)</option>
                <option value="regNo_desc" className="bg-slate-900">Sort by: Reg ID (Desc)</option>
                <option value="loginTime_desc" className="bg-slate-900">Sort by: Login Time (Latest)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table View */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-black/40 text-slate-400 uppercase text-[10px] font-bold border-b border-white/10">
              <tr>
                <th className="p-3.5 cursor-pointer hover:text-white" onClick={() => handleSortClick('score')}>
                  # {renderSortIndicator('score')}
                </th>
                <th className="p-3.5 cursor-pointer hover:text-white" onClick={() => handleSortClick('name')}>
                  Candidate Details {renderSortIndicator('name')}
                </th>
                <th className="p-3.5 cursor-pointer hover:text-white" onClick={() => handleSortClick('loginTime')}>
                  Lobby Login {renderSortIndicator('loginTime')}
                </th>
                <th className="p-3.5 text-center">
                  Exam Activity Status
                </th>
                <th className="p-3.5 text-center cursor-pointer hover:text-white" onClick={() => handleSortClick('answers')}>
                  Answers {renderSortIndicator('answers')}
                </th>
                <th className="p-3.5 text-center cursor-pointer hover:text-white" onClick={() => handleSortClick('score')}>
                  Score {renderSortIndicator('score')}
                </th>
                <th className="p-3.5 text-center cursor-pointer hover:text-white" onClick={() => handleSortClick('percentage')}>
                  Percentage {renderSortIndicator('percentage')}
                </th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-slate-200">
              {processedAttempts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    No candidates found matching the selected filter criteria.
                  </td>
                </tr>
              ) : (
                processedAttempts.map((att, idx) => {
                  const answeredCount = Object.keys(att.answers || {}).length || ((att.correctCount || 0) + (att.wrongCount || 0)) || (att.score > 0 ? (att.totalQuestions || 30) : 0);
                  const isExamGiven = att.actuallyGaveExam !== undefined ? att.actuallyGaveExam : (answeredCount > 0 || (att.score || 0) > 0);

                  return (
                    <tr key={att.attemptId || idx} className="hover:bg-white/5 transition">
                      <td className="p-3.5 text-slate-400 font-mono font-bold">{idx + 1}</td>
                      <td className="p-3.5">
                        <strong className="text-white block text-sm">{att.studentName}</strong>
                        <span className="text-amber-300 font-mono text-[11px] block">
                          {att.studentRegistrationNumber}
                        </span>
                        <span className="text-slate-400 text-[10px]">
                          {att.studentMobile} • {att.studentBatchLabel || 'Unified Batch'}
                        </span>
                      </td>
                      <td className="p-3.5 text-slate-300 font-mono text-[11px]">
                        {att.loginTime || '13:00 - 13:15 IST'}
                      </td>
                      <td className="p-3.5 text-center">
                        {(att.status === 'submitted' || isExamConcluded) && isExamGiven ? (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700 inline-flex items-center space-x-1">
                            <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Submitted &amp; Final</span>
                          </span>
                        ) : (!isExamConcluded && (att.isLiveActive || att.status === 'in_progress')) ? (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 inline-flex items-center space-x-1.5 animate-pulse">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span>Live In-Test Now</span>
                          </span>
                        ) : isExamGiven ? (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700 inline-flex items-center space-x-1">
                            <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Submitted &amp; Final</span>
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-400 border border-slate-700 inline-flex items-center space-x-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                            <span>Lobby Only (0 Answers)</span>
                          </span>
                        )}
                      </td>
                      <td className="p-3.5 text-center font-mono">
                        <span className="text-white font-bold">{answeredCount}</span>
                        <span className="text-[10px] text-slate-400"> / {att.totalQuestions || 30}</span>
                      </td>
                      <td className="p-3.5 text-center font-mono font-bold">
                        <span className="text-amber-400 text-sm">
                          {att.score} <span className="text-[10px] text-slate-400">/ {att.totalQuestions || 30}</span>
                        </span>
                      </td>
                      <td className="p-3.5 text-center font-mono font-bold">
                        <span className={att.percentage >= 50 ? 'text-emerald-400' : (isExamGiven ? 'text-orange-400' : 'text-slate-500')}>
                          {att.percentage}%
                        </span>
                        {isExamGiven && (
                          <span className={`block text-[9px] uppercase tracking-wider ${att.percentage >= 50 ? 'text-emerald-300 font-black' : 'text-orange-300'
                            }`}>
                            {att.percentage >= 50 ? 'QUALIFIED' : 'NEEDS IMPROVEMENT'}
                          </span>
                        )}
                      </td>
                      <td className="p-3.5 text-right">
                        <button
                          onClick={() => setInspectAttempt(att)}
                          className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-[11px] transition inline-flex items-center space-x-1 cursor-pointer"
                        >
                          <EyeIcon className="w-3.5 h-3.5" />
                          <span>View Sheet</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
      )}

      {/* Answer Sheet Inspection Modal */}
      {inspectAttempt && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/20 rounded-3xl p-6 sm:p-8 max-w-3xl w-full max-h-[85vh] flex flex-col shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <h3 className="text-lg font-black text-white">
                  Candidate Answer Sheet: {inspectAttempt.studentName}
                </h3>
                <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs font-mono">
                  <span className="text-amber-400 font-bold">
                    Reg ID: {inspectAttempt.studentRegistrationNumber}
                  </span>
                  <span className="text-slate-500">•</span>
                  <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 font-bold">
                    Answered: {Object.keys(inspectAttempt.answers || {}).length || ((inspectAttempt.correctCount || 0) + (inspectAttempt.wrongCount || 0)) || (inspectAttempt.actuallyGaveExam ? 30 : 0)} / {inspectAttempt.totalQuestions || 30}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold">
                    Correct: {inspectAttempt.correctCount || 0}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 font-bold">
                    Wrong: {inspectAttempt.wrongCount || 0}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-black">
                    Final Score: {inspectAttempt.score} / {inspectAttempt.totalQuestions || 30} ({inspectAttempt.percentage}%)
                  </span>
                  {inspectAttempt.tabSwitchCount !== undefined && inspectAttempt.tabSwitchCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 font-bold">
                      Tab Switches: {inspectAttempt.tabSwitchCount}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setInspectAttempt(null)}
                className="px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition cursor-pointer"
              >
                Close
              </button>
            </div>

            {/* Questions List */}
            <div className="overflow-y-auto space-y-3 pr-2 flex-1">
              {inspectAttempt.detailedResponses ? (
                Array.from({ length: 30 }, (_, i) => i + 1).map((num) => {
                  const qText = inspectAttempt.detailedResponses[`Q${num} Question`];
                  if (!qText) return null;
                  const candidateAns = inspectAttempt.detailedResponses[`Q${num} Candidate Answer`];
                  const correctKey = inspectAttempt.detailedResponses[`Q${num} Correct Key`];
                  const mark = inspectAttempt.detailedResponses[`Q${num} Mark`];
                  const isUnattempted = !candidateAns || candidateAns === 'UNATTEMPTED';
                  const isCorrect = mark === 1;

                  return (
                    <div key={num} className="p-4 rounded-xl bg-black/40 border border-white/10 text-xs space-y-2">
                      <div className="flex justify-between items-center text-slate-400">
                        <span className="font-bold">Q{num}.</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          isCorrect ? 'bg-emerald-500/20 text-emerald-400' : isUnattempted ? 'bg-slate-800 text-slate-400' : 'bg-rose-500/20 text-rose-400'
                        }`}>
                          {isCorrect ? 'Correct (+1)' : isUnattempted ? 'Unattempted (0)' : 'Incorrect (0)'}
                        </span>
                      </div>

                      <p className="font-semibold text-white">{qText}</p>

                      <div className="grid grid-cols-1 gap-1.5">
                        <div className={`p-2 rounded-lg border flex items-center justify-between ${
                          isCorrect ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300 font-semibold' :
                          isUnattempted ? 'bg-slate-800/60 border-slate-700 text-slate-400' :
                          'bg-rose-500/20 border-rose-500/60 text-rose-300 font-semibold'
                        }`}>
                          <span>Candidate's Response: {candidateAns || 'UNATTEMPTED'}</span>
                          {isCorrect ? (
                            <span className="text-[10px] uppercase font-bold text-emerald-300">[Correct]</span>
                          ) : isUnattempted ? (
                            <span className="text-[10px] uppercase font-bold text-slate-400">[Skipped]</span>
                          ) : (
                            <span className="text-[10px] uppercase font-bold text-rose-300">[Incorrect]</span>
                          )}
                        </div>
                        {!isCorrect && correctKey && (
                          <div className="p-2 rounded-lg border bg-emerald-500/10 border-emerald-500/40 text-emerald-300 flex items-center justify-between">
                            <span>Official Key: {correctKey}</span>
                            <span className="text-[10px] uppercase font-bold text-emerald-300">[Key]</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                ((inspectAttempt.assignedQuestionIds && inspectAttempt.assignedQuestionIds.length > 0)
                  ? inspectAttempt.assignedQuestionIds.map((id: number) => currentExamConfig.questions.find(q => q.id === id)).filter((q: any): q is ExamQuestion => Boolean(q))
                  : currentExamConfig.questions.slice(0, inspectAttempt.totalQuestions || 30)
                ).map((q: ExamQuestion, idx: number) => {
                  const userAns = inspectAttempt.answers ? inspectAttempt.answers[q.id] : undefined;
                  const isCorrect = userAns === q.correctOption;
                  const isUnanswered = userAns === undefined || userAns === null;

                  return (
                    <div key={q.id} className="p-4 rounded-xl bg-black/40 border border-white/10 text-xs space-y-2">
                      <div className="flex justify-between items-center text-slate-400">
                        <span className="font-bold">Q{idx + 1}. {q.category}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isCorrect ? 'bg-emerald-500/20 text-emerald-400' : isUnanswered ? 'bg-slate-800 text-slate-400' : 'bg-rose-500/20 text-rose-400'
                          }`}>
                          {isCorrect ? 'Correct (+1)' : isUnanswered ? 'Unattempted (0)' : 'Incorrect (0)'}
                        </span>
                      </div>

                      <p className="font-semibold text-white">{q.text}</p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {q.options.map((opt, optIdx) => {
                          const isChosen = userAns === optIdx;
                          const isKey = q.correctOption === optIdx;

                          let style = 'bg-white/5 border-white/5 text-slate-400';
                          if (isKey) style = 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300 font-semibold';
                          else if (isChosen && !isKey) style = 'bg-rose-500/20 border-rose-500/60 text-rose-300 font-semibold';

                          return (
                            <div key={optIdx} className={`p-2 rounded-lg border flex items-center justify-between ${style}`}>
                              <span>{String.fromCharCode(65 + optIdx)}. {opt}</span>
                              {isChosen && <span className="text-[10px] uppercase font-bold text-amber-300">[Chosen]</span>}
                              {isKey && <span className="text-[10px] uppercase font-bold text-emerald-300">[Key]</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExamManagement;
